import classnames from 'classnames';
import Image from 'components/Image';
import Overlay from 'components/Modal/Overlay';
import CloseIcon from 'components/icons/CloseIcon';
import PauseIcon from 'components/icons/PauseIcon';
import useIsMobile from 'hooks/useIsMobile';
import React, { useEffect, useRef, useState } from 'react';
import { isIosWeb } from 'utils/platform';

const CSS = require('./VideoModal.module.scss');

const POSTER_WHITE =
  'https://withlocals-com-res.cloudinary.com/image/upload/v1566233179/host/intro-video/wl-video-start-white.png';
const POSTER_BLACK =
  'https://withlocals-com-res.cloudinary.com/image/upload/v1566232429/host/intro-video/wl-video-start-black.png';

const POSTER_OPTIONS = {
  width: 960,
  height: 960,
  quality: 80,
};

const doNothing = () => {};

interface VideoModalProps {
  onClose: () => void;
  videoUrl: string;
  isWhite?: boolean;
  tryFetchSubtitles?: boolean;
  isHostVideo?: boolean;
  onStart?: () => void;
  onVideoPause?: () => void;
  showControls?: boolean;
  showAsPage?: boolean;
}

function getSubtitleTrack(video) {
  const subtitleTrack = video
    .replace('/video/', '/raw/')
    .replace(new RegExp('/v[0-9]{10}/'), '/')
    .replace('.mp4', '.vtt')
    .replace('.webm', '.vtt');
  return (
    <track
      label="English"
      kind="subtitles"
      srcLang="en"
      src={subtitleTrack}
      default
    />
  );
}

function getVideoUrl(videoUrl, isHostVideo, type) {
  if (isHostVideo) {
    return videoUrl.replace('.mp4', ``);
  }
  const video = videoUrl.split('upload');
  return `${video[0]}upload/w_960,f_${type},q_auto${video[1]}`;
}

function Video({
  onClose,
  videoUrl,
  isWhite,
  tryFetchSubtitles,
  isHostVideo,
  onStart,
  onVideoPause,
}: VideoModalProps) {
  const [isReady, setVideoReady] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const video = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const timeout = setTimeout(() => setVideoReady(true), 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [video]);

  const onPlay = () => {
    setPlaying(true);
  };

  const togglePlay = () => {
    if (isPlaying) {
      video.current.pause();
    } else {
      video.current.play();
    }
  };

  const setReadyToPlay = () => {
    setVideoReady(true);
    if (onStart) {
      onStart();
    }
  };

  const onPause = () => {
    setPlaying(false);
    if (onVideoPause) {
      onVideoPause();
    }
    return isMobile ? onClose() : null;
  };

  const onEnded = () => {
    onClose();
  };
  return (
    <div>
      <button className={CSS.CloseIconContainer} onClick={onClose}>
        <CloseIcon
          className={classnames(CSS.CloseIcon, {
            [CSS.CloseIconAlternative]: isWhite && !isIosWeb(),
          })}
        />
      </button>
      <div className={CSS.VideoContainer} onClick={togglePlay}>
        <video
          onEnded={onEnded}
          onPause={onPause}
          onPlay={onPlay}
          className={CSS.Video}
          preload="auto"
          autoPlay
          onCanPlay={setReadyToPlay}
          crossOrigin={tryFetchSubtitles ? 'anonymous' : ''}
          playsInline
          ref={video}
        >
          <source
            src={`${getVideoUrl(videoUrl, isHostVideo, 'webm')}.webm`}
            type="video/webm"
          />
          <source
            src={`${getVideoUrl(videoUrl, isHostVideo, 'mp4')}.mp4`}
            type="video/mp4"
          />
          {tryFetchSubtitles && getSubtitleTrack(videoUrl)}
        </video>
        {isReady && !isPlaying && (
          <div className={CSS.PauseContainer}>
            <PauseIcon
              className={CSS.PauseIcon}
              fill="white"
              width={64}
              height={64}
            />
            <span className={CSS.Continue}>Click to continue</span>
          </div>
        )}
        <Image
          className={classnames(CSS.Poster, { [CSS.PosterAnimate]: isReady })}
          src={isWhite && !isIosWeb() ? POSTER_WHITE : POSTER_BLACK}
          width={POSTER_OPTIONS.width}
          height={POSTER_OPTIONS.height}
          options={POSTER_OPTIONS}
        />
      </div>
    </div>
  );
}

function VideoModal({
  onClose,
  videoUrl,
  isWhite,
  tryFetchSubtitles,
  isHostVideo,
  onStart,
  onVideoPause,
  showAsPage,
}: VideoModalProps) {
  const classes = classnames(CSS.PageOverlay, {
    [CSS.PageOverlay]: isWhite && !isIosWeb(),
  });
  if (showAsPage) {
    return (
      <div className={classes}>
        <Video
          onClose={onClose}
          videoUrl={videoUrl}
          isWhite={isWhite}
          tryFetchSubtitles={tryFetchSubtitles}
          isHostVideo={isHostVideo}
          onStart={onStart}
          onVideoPause={onVideoPause}
        />
      </div>
    );
  }
  return (
    <Overlay
      portalId="videoModal"
      onEsc={onClose}
      onClick={doNothing}
      className={classes}
    >
      <Video
        onClose={onClose}
        videoUrl={videoUrl}
        isWhite={isWhite}
        tryFetchSubtitles={tryFetchSubtitles}
        isHostVideo={isHostVideo}
        onStart={onStart}
        onVideoPause={onVideoPause}
      />
    </Overlay>
  );
}

export default VideoModal;
